import {inject} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivateFn} from '@angular/router';
import {AuthService} from '@ew/data-access';

/**
 * Authentication guard for protecting routes from unauthorized access.
 *
 * This functional guard checks if the user is authenticated before allowing access to a route.
 * It's used in Angular route configurations to protect routes that require authentication.
 *
 * The guard:
 * - Checks for a valid authentication token
 * - Validates the token with the backend
 * - Redirects to login page if not authenticated
 * - Stores user information globally if authenticated
 * - Handles token from URL query parameters (for OAuth/SSO flows)
 *
 * @example
 * // In route configuration
 * const routes: Routes = [
 *   {
 *     path: 'dashboard',
 *     component: DashboardComponent,
 *     canActivate: [authGuard]
 *   }
 * ];
 *
 * @param {ActivatedRouteSnapshot} route - The route being activated
 * @returns {Promise<boolean>} True if authenticated, false otherwise
 *
 * @see AuthService - The authentication service used for validation
 * @see GlobalUserStore - Where user information is stored after authentication
 */
export const authGuard: CanActivateFn = async (route: ActivatedRouteSnapshot): Promise<boolean> => {
  const authService = inject(AuthService);

  try {
    return await authService.isAuthenticated(route);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    return false;
  }
};
